/* ============================================================
   GLOP — the butterfly cursor, drawn as an element.
   The PNG cursor in glop.css blinks as it moves: the browser re-sets the
   cursor image as the mouse crosses elements. Once this runs, the system cursor
   is hidden (html.glop-cursor-on) and one butterfly follows the mouse, so it
   stays solid. It turns pink over anything clickable, cream on the charcoal
   footer, and hands back to the I-beam over text fields. It's a manual popover,
   so it sits in the top layer above modals (cart drawer, notify pop-up); it is
   re-raised whenever a dialog opens. Mouse/trackpad only; without JS the
   PNG cursor stays.
   ============================================================ */
(() => {
  if (!matchMedia('(pointer: fine)').matches) return;

  const SIZE = 34; // px; art is 32 × 32 with the head at (11, 2)
  const HOT_X = (11 / 32) * SIZE, HOT_Y = (2 / 32) * SIZE;
  const CLICKABLE = 'a[href], button:not([disabled]), summary, label[for], select, [role="button"], [on\\:click], input[type="submit"], input[type="button"], input[type="checkbox"], input[type="radio"], .glop-btn';
  const TEXT = 'input:not([type="submit"]):not([type="button"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="color"]):not([type="file"]), textarea, [contenteditable="true"]';

  let el = null, x = -100, y = -100, state = '', hidden = true;
  const top = typeof HTMLElement.prototype.showPopover === 'function';

  function build() {
    el = document.createElement('div');
    el.className = 'glop-cursor';
    el.setAttribute('aria-hidden', 'true');
    if (top) el.setAttribute('popover', 'manual');
    document.body.append(el);
    // warm the pink and cream art so the first swap doesn't blank for a frame
    const url = getComputedStyle(el).backgroundImage.match(/url\("?([^")]+)"?\)/);
    if (url) ['-pink-2x', '-cream-2x'].forEach((v) => { new Image().src = url[1].replace('-2x', v); });
    raise();
    document.documentElement.classList.add('glop-cursor-on');
    // a dialog opening joins the top layer above us: step back on top of it
    new MutationObserver(raise).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
  }

  function raise() {
    if (!top || !el) return;
    try {
      if (el.matches(':popover-open')) el.hidePopover();
      el.showPopover();
    } catch (e) {
      /* not connected yet */
    }
  }

  function look(target) {
    let next = 'dark';
    if (!(target instanceof Element)) next = 'dark';
    else if (target.closest(TEXT)) next = 'text';
    else if (target.closest(CLICKABLE)) next = 'pink';
    else if (target.closest('.glop2-footer')) next = 'cream';
    if (next !== state) {
      state = next;
      el.dataset.state = next;
    }
  }

  addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
    if (!el) build();
    x = e.clientX; y = e.clientY;
    el.style.transform = `translate3d(${x - HOT_X}px, ${y - HOT_Y}px, 0)`;
    look(e.target);
    if (hidden) { hidden = false; el.classList.add('is-on'); }
  }, { passive: true });

  // hovered element changed under a still mouse (menu opened, scroll)
  addEventListener('pointerover', (e) => { if (el) look(e.target); }, { passive: true });
  // left the window
  addEventListener('mouseout', (e) => { if (el && !e.relatedTarget) { hidden = true; el.classList.remove('is-on'); } });
  addEventListener('blur', () => { if (el) { hidden = true; el.classList.remove('is-on'); } });
})();

/* ============================================================
   GLOP — fairy dust behind the butterfly cursor.
   A few tiny glints shed from the wings as the mouse moves, drift down and
   fade in about a second. One fixed canvas, pointer-events: none, built on the
   first move; it only animates while glints are alive. Mouse/trackpad only,
   nothing under prefers-reduced-motion.
   ============================================================ */
(() => {
  if (!matchMedia('(pointer: fine)').matches) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const MAX = 70; // glints alive at once
  const STEP = 11; // px of travel per glint
  const COLORS = ['#ffa2dc', '#ffa2dc', '#f2c9e6', '#f5cf7a', '#c9b8f2', '#c4c8cc'];

  let cv = null, ctx = null, dpr = 1, running = false, last = null, travel = 0, prevT = 0;
  const dust = [];

  function build() {
    cv = document.createElement('canvas');
    cv.className = 'glop-dust';
    cv.setAttribute('aria-hidden', 'true');
    cv.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483000;';
    document.body.append(cv);
    ctx = cv.getContext('2d');
    size();
    addEventListener('resize', size, { passive: true });
  }

  function size() {
    dpr = Math.min(devicePixelRatio || 1, 2);
    cv.width = Math.round(innerWidth * dpr);
    cv.height = Math.round(innerHeight * dpr);
  }

  const rand = (a, b) => a + Math.random() * (b - a);

  function spawn(x, y, dx, dy) {
    if (dust.length >= MAX) dust.shift();
    const star = Math.random() < 0.18;
    dust.push({
      /* the hotspot is the head; shed from the wings below and right of it */
      x: x + rand(3, 17),
      y: y + rand(8, 24),
      vx: rand(-12, 12) - dx * 0.6,
      vy: rand(-10, 6) - dy * 0.3,
      r: star ? rand(2.6, 3.8) : rand(0.7, 1.7),
      star,
      c: COLORS[(Math.random() * COLORS.length) | 0],
      age: 0,
      life: rand(0.7, 1.15),
      tw: rand(9, 16),
      ph: rand(0, 6.28),
    });
  }

  function drawStar(p, a) {
    const r = p.r, t = r * 0.22;
    ctx.globalAlpha = a;
    ctx.fillStyle = p.c;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y - r);
    ctx.quadraticCurveTo(p.x + t, p.y - t, p.x + r, p.y);
    ctx.quadraticCurveTo(p.x + t, p.y + t, p.x, p.y + r);
    ctx.quadraticCurveTo(p.x - t, p.y + t, p.x - r, p.y);
    ctx.quadraticCurveTo(p.x - t, p.y - t, p.x, p.y - r);
    ctx.fill();
  }

  function frame(t) {
    const dt = Math.min(0.05, (t - prevT) / 1000 || 0.016);
    prevT = t;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cv.width, cv.height);
    for (let i = dust.length - 1; i >= 0; i--) {
      const p = dust[i];
      p.age += dt;
      if (p.age >= p.life) { dust.splice(i, 1); continue; }
      p.vy += 28 * dt; // a gentle settle downward
      p.vx *= 1 - 1.8 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      const k = p.age / p.life;
      const fade = k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85;
      const a = 0.85 * fade * (0.65 + 0.35 * Math.sin(p.age * p.tw + p.ph));
      if (p.star) drawStar(p, a);
      else {
        ctx.fillStyle = p.c;
        ctx.globalAlpha = a * 0.25;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 2.4, 0, 6.2832); ctx.fill();
        ctx.globalAlpha = a;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.2832); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    if (dust.length && !document.hidden) requestAnimationFrame(frame);
    else { running = false; ctx.clearRect(0, 0, cv.width, cv.height); dust.length = 0; }
  }

  addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
    const x = e.clientX, y = e.clientY;
    if (!last) { last = { x, y }; return; }
    const dx = x - last.x, dy = y - last.y;
    last = { x, y };
    travel += Math.hypot(dx, dy);
    if (travel < STEP) return;
    if (!cv) build();
    let n = Math.min(3, Math.floor(travel / STEP));
    travel = Math.min(travel - n * STEP, STEP);
    while (n--) spawn(x, y, dx, dy);
    if (!running) { running = true; prevT = performance.now(); requestAnimationFrame(frame); }
  }, { passive: true });

  document.addEventListener('pointerleave', () => { last = null; travel = 0; });
})();
